import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { TeamScopeContext, type TeamScopeValue } from "./TeamScope";
import { useAuth } from "./useAuth";
import { formatApiErrorForUi } from "../services/errorUtils";
import { fetchTeamNames } from "../features/teams/fetchTeamNames";
import { fetchCurrentMember } from "../features/identity/fetchCurrentMember";
import type { TeamRole } from "../features/identity/TeamRole";

const TEAM_STORAGE_KEY = "dartsstats.team";

/** Storage is unavailable in private mode on some browsers; a lost preference is not worth a crash. */
function readStored(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignored on purpose - see readStored.
  }
}

/**
 * Asks the server who the caller is, and swallows the answers that only mean "not yet".
 *
 * A 403 is not one of those: it says this account belongs to no player record, and falling
 * back to the team list would then show somebody a team they have no claim to. That one is
 * rethrown so it reaches the screen.
 */
async function tryFetchMember(token: string | null) {
  try {
    return await fetchCurrentMember(token);
  } catch (err) {
    if ((err as { status?: number }).status === 403) throw err;
    return null;
  }
}

export default function TeamScopeProvider({ children }: { children: ReactNode }) {
  const [teams, setTeams] = useState<string[]>([]);
  const [team, setTeamState] = useState<string>(() => readStored(TEAM_STORAGE_KEY));
  const [role, setRole] = useState<TeamRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    void (async () => {
      try {
        setLoading(true);
        const token = await getAccessToken();

        // Who am I, according to the server. Until Easy Auth sits in front of the function
        // apps this cannot be verified, and then the old path still has to work - otherwise
        // deploying this would lock everybody out.
        const member = await tryFetchMember(token);
        if (cancelled) return;

        if (member?.isVerified && member.teamName) {
          // Exactly one team, the caller's own. There is nothing to offer and nothing to
          // switch to: another team is not theirs to look at.
          setTeams([member.teamName]);
          setTeamState(member.teamName);
          setRole(member.role);
          return;
        }

        const names = await fetchTeamNames(token);
        if (cancelled) return;

        setTeams(names);
        // One team is the normal case for a club, and then there is nothing to
        // choose. A remembered team that no longer exists falls back the same way.
        setTeamState((current) => (current && names.includes(current) ? current : (names[0] ?? "")));
      } catch (err) {
        if (!cancelled) setError(formatApiErrorForUi(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getAccessToken, isAuthenticated]);

  const setTeam = useCallback((next: string) => {
    setTeamState(next);
    writeStored(TEAM_STORAGE_KEY, next);
  }, []);

  const value = useMemo<TeamScopeValue>(
    () => ({
      team,
      teams,
      role,
      // The ladder has to read the same way as on the server: an administrator may everything
      // a captain may. Only a stated role counts - a null role is unknown, and unknown is not
      // a yes.
      isCaptain: role === "Kapitaen" || role === "Admin",
      isAdmin: role === "Admin",
      loading,
      error,
      setTeam,
    }),
    [team, teams, role, loading, error, setTeam],
  );

  return <TeamScopeContext.Provider value={value}>{children}</TeamScopeContext.Provider>;
}
